import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { fetchMoneyData } from "@/hooks/useMoneyFoundation";
import { computeMoneyStats, foundationRequirements } from "@/lib/desk/foundations";
import {
  Seat,
  DeskCounters,
  EMPTY_COUNTERS,
  PromotionStatus,
  SEATS,
  evaluatePromotion,
  nextSeat,
  seatAtLeast,
  seatUnlocks,
  normalizeSeat,
} from "@/lib/desk/career";

import { usePracticeMode } from "@/hooks/usePracticeMode";
import { getPractice, updatePractice } from "@/lib/practice/store";
import { completedLevels, completedAnalystLevels, completedRealEstateLevels } from "@/lib/desk/internAcademy";

const TRACK = "trade";

function practiceCareer(): DeskCareer {
  const p = getPractice();
  const wisdom = Object.values(p.decisions).reduce((a, d) => a + d.wisdom, 0);
  const counters: DeskCounters = { ...EMPTY_COUNTERS, ...p.desk, wisdom, academyUnits: Math.max(p.desk.academyUnits ?? 0, completedLevels(p.academy ?? [], p.homework ?? [])), analystLevels: Math.max((p.desk as any).analystLevels ?? 0, completedAnalystLevels(p.academy ?? [], p.homework ?? [])), realEstateLevels: completedRealEstateLevels(p.academy ?? [], p.homework ?? []) };
  return {
    seat: normalizeSeat(p.seat),
    promotedAt: null,
    counters,
    status: evaluatePromotion(normalizeSeat(p.seat), counters, foundationRequirements(p.seat, computeMoneyStats(p.checkins, p.habits, p.lessons, (p as any).override, (p as any).live))),
  };
}

export interface DeskCareer {
  seat: Seat;
  promotedAt: string | null;
  counters: DeskCounters;
  status: PromotionStatus;
}

/**
 * The member's seat on the desk, plus the live counters behind the promotion
 * checklist. Counters are derived from real work — theses, reviews, closed
 * paper trades — never from a stored score that could drift out of step.
 */
export function useDeskCareer() {
  const { user } = useAuth();
  const uid = user?.id;
  const practice = usePracticeMode();

  const query = useQuery({
    queryKey: ["desk-career", uid, practice],
    enabled: !!uid,
    queryFn: async (): Promise<DeskCareer> => {
      if (practice) return practiceCareer();
      const [progressionRes, modulesRes, thesesRes, tradesRes, wisdomRes, money] = await Promise.all([
        supabase
          .from("desk_progression")
          .select("seat,promoted_at")
          .eq("user_id", uid!)
          .eq("track_id", TRACK)
          .maybeSingle(),
        supabase
          .from("learning_module_progress")
          .select("module_key")
          .eq("user_id", uid!)
          .eq("track_id", TRACK)
          .not("completed_at", "is", null),
        supabase.from("theses").select("id").eq("author_id", uid!),
        supabase
          .from("paper_trades")
          .select("id,lessons_learned,followed_plan")
          .eq("user_id", uid!)
          .eq("is_closed", true),
        (supabase as any).from("sim_decisions").select("wisdom").eq("user_id", uid!),
        fetchMoneyData(uid!),
      ]);
      const [journalRes, simTradesRes, academyRes, chartsRes, homeworkRes] = await Promise.all([
        (supabase as any).from("sim_theses").select("status, created_at").eq("user_id", uid!),
        (supabase as any).from("sim_trades").select("lesson, followed_plan, closed_at, opened_by, sim_accounts!inner(kind,user_id)").eq("opened_by", uid!).not("closed_at", "is", null).eq("sim_accounts.kind", "personal"),
        (supabase as any).from("intern_academy_progress").select("lesson_id").eq("user_id", uid!),
        supabase.from("chart_analyses").select("was_successful, followed_up_at").eq("user_id", uid!),
        (supabase as any).from("academy_homework_submissions").select("assignment_id").eq("user_id", uid!).eq("status", "passed"),
      ]);
      // Auto-graded journal entries count alongside older theses.
      const graded = await (supabase as any).from("trade_thesis_journals").select("status, created_at").eq("user_id", uid!).not("judge_rule", "is", null);
      const journal = [...((journalRes?.data ?? []) as { status: string; created_at: string }[]), ...((graded?.error ? [] : graded?.data ?? []) as { status: string; created_at: string }[])];
      const simTrades = ((simTradesRes?.data ?? []) as { lesson: string | null; followed_plan: boolean | null }[]);
      const wk = (d: string) => Math.floor(new Date(d).getTime() / 6048e5);

      const seat = normalizeSeat(progressionRes.data?.seat);
      // Old tracker chart reviews carry over: a review counts once its follow-up check is logged.
      const charts = ((chartsRes?.data ?? []) as { was_successful: boolean | null; followed_up_at: string | null }[]).filter((c) => c.followed_up_at);
      const thesisIds = (thesesRes.data ?? []).map((t) => t.id);

      let soundReviews = 0;
      if (thesisIds.length) {
        const { data: reviews } = await supabase
          .from("thesis_reviews")
          .select("thesis_id")
          .in("thesis_id", thesisIds)
          .eq("rating", "sound");
        // Count distinct theses rated sound, not raw review rows — five
        // reviewers agreeing on one thesis is still one piece of sound work.
        soundReviews = new Set((reviews ?? []).map((r) => r.thesis_id)).size;
      }

      const trades = tradesRes.data ?? [];
      const counters: DeskCounters = {
        modulesComplete: (modulesRes.data ?? []).length,
        academyUnits: completedLevels(((academyRes?.data ?? []) as { lesson_id: string }[]).map((r) => r.lesson_id), ((homeworkRes?.data ?? []) as { assignment_id: string }[]).map((r) => r.assignment_id)),
        analystLevels: completedAnalystLevels(((academyRes?.data ?? []) as { lesson_id: string }[]).map((r) => r.lesson_id), ((homeworkRes?.data ?? []) as { assignment_id: string }[]).map((r) => r.assignment_id)),
        realEstateLevels: completedRealEstateLevels(((academyRes?.data ?? []) as { lesson_id: string }[]).map((r) => r.lesson_id), ((homeworkRes?.data ?? []) as { assignment_id: string }[]).map((r) => r.assignment_id)),
        wisdom: ((wisdomRes?.data ?? []) as { wisdom: number }[]).reduce((a, r) => a + (r.wisdom || 0), 0),
        thesesPublished: thesisIds.length,
        soundReviews,
        closedTrades: trades.length + simTrades.length,
        journaledTrades: trades.filter((t) => (t.lessons_learned ?? "").trim().length > 0).length + simTrades.filter((t) => (t.lesson ?? "").trim()).length,
        disciplinedTrades: trades.filter((t) => t.followed_plan === true).length + simTrades.filter((t) => t.followed_plan).length,
        journalTheses: journal.length,
        journalWeeks: new Set(journal.map((j) => wk(j.created_at))).size,
        journalScored: journal.filter((j) => j.status === "win" || j.status === "loss").length,
        journalWins: journal.filter((j) => j.status === "win").length,
        chartsReviewed: charts.length,
        chartsScored: charts.filter((c) => c.was_successful != null).length,
        chartWins: charts.filter((c) => c.was_successful === true).length,
      };

      return {
        seat,
        promotedAt: progressionRes.data?.promoted_at ?? null,
        counters,
        status: evaluatePromotion(
          seat,
          counters,
          foundationRequirements(seat, computeMoneyStats(money.checkins, money.habits, money.lessons, (money as any).override, (money as any).live)),
        ),
      };
    },
  });

  const seat: Seat = query.data?.seat ?? "intern";
  const counters = query.data?.counters ?? EMPTY_COUNTERS;
  const status =
    query.data?.status ??
    evaluatePromotion(seat, counters, foundationRequirements(seat, computeMoneyStats([], null, [])));

  return {
    seat,
    seatDef: SEATS[seat],
    counters,
    status,
    promotedAt: query.data?.promotedAt ?? null,
    isLoading: query.isLoading,
    /** Is this tool open at the member's current seat? */
    can: (tool: string) => seatUnlocks(seat, tool),
    atLeast: (required: Seat) => seatAtLeast(seat, required),
  };
}

export interface PromotionApplication {
  id: string;
  user_id: string;
  from_seat: string;
  to_seat: string;
  snapshot: any;
  defense: string;
  status: "pending" | "approved" | "returned";
  review_note: string | null;
  reviewer_id: string | null;
  created_at: string;
  reviewed_at: string | null;
}

/** The member's most recent promotion application (any status). */
export function useMyApplication() {
  const { user } = useAuth();
  const practice = usePracticeMode();
  return useQuery({
    queryKey: ["desk-application", user?.id, practice],
    enabled: !!user && !practice,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("desk_promotion_applications")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data ?? null) as PromotionApplication | null;
    },
  });
}

/** Pending applications waiting on a Senior Strategist or admin. RLS hides them from everyone else. */
export function useApplicationQueue(all = false) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["desk-application-queue", all],
    enabled: !!user,
    queryFn: async () => {
      let q = (supabase as any).from("desk_promotion_applications").select("*").order("created_at", { ascending: false }).limit(200);
      if (!all) q = q.eq("status", "pending").neq("user_id", user!.id);
      const { data } = await q;
      const apps = (data ?? []) as PromotionApplication[];
      const ids = [...new Set(apps.map((a) => a.user_id))];
      const names: Record<string, string> = {};
      if (ids.length) {
        const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
        (profs ?? []).forEach((p: any) => (names[p.user_id] = p.full_name || "Student"));
      }
      return apps.map((a) => ({ ...a, name: names[a.user_id] ?? "Student" }));
    },
  });
}

export function useReviewApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; decision: "approve" | "return"; note: string }) => {
      const { data, error } = await supabase.functions.invoke("desk-promotion", { body: { action: "review", ...v } });
      if (error) throw new Error((data as any)?.error ?? error.message);
      if ((data as any)?.error) throw new Error((data as any).error);
      return v.decision;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["desk-application-queue"] });
      toast.success(d === "approve" ? "Approved — the seat is theirs." : "Returned with your note.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * Apply for the next seat. The member has to choose this — finishing the
 * checklist never promotes on its own. The desk-promotion function re-checks
 * the work, then either confirms the seat (Intern to Analyst) or queues it
 * for a Senior Strategist.
 */
export function usePromote() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const practice = usePracticeMode();

  return useMutation({
    mutationFn: async ({
      from,
      defense,
      snapshot,
    }: {
      from: Seat;
      defense: string;
      snapshot?: unknown;
    }): Promise<{ promotedTo: Seat | null; status: string; note?: string | null }> => {
      if (!user) throw new Error("You must be signed in.");
      const to = nextSeat(from);
      if (!to) throw new Error("You already hold the senior seat.");
      if (defense.trim().length < 80) {
        throw new Error("Your defense needs more than a sentence — write at least 80 characters.");
      }
      if (practice) {
        updatePractice((d) => ({ ...d, seat: to }));
        return { promotedTo: to, status: "approved" };
      }
      const { data, error } = await supabase.functions.invoke("desk-promotion", {
        body: { action: "apply", defense: defense.trim(), oath: true, snapshot, prompt: SEATS[from].defensePrompt },
      });
      if ((data as any)?.error) throw new Error((data as any).error);
      if (error) throw new Error(error.message);
      const app = (data as any).application;
      return { promotedTo: ((data as any).promotedTo as Seat) ?? null, status: app?.status, note: app?.review_note };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["desk-career"] });
      queryClient.invalidateQueries({ queryKey: ["desk-application"] });
      queryClient.invalidateQueries({ queryKey: ["hf-rank"] });
      if (r.status === "pending") toast.success("Application submitted. A Senior Strategist will review it.");
      else if (r.status === "returned") toast.error(r.note || "Returned — add more of your own thinking.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
